import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { UserAvatar } from '@/components/UserAvatar';
import ProjectSearchPicker from '@/components/ProjectSearchPicker';
import BreadcrumbNav from '@/components/BreadcrumbNav';
import { 
  ArrowLeft, Mail, Calendar, Clock, MapPin, Home, Building2,
  Flame, ThermometerSun, Snowflake, Trophy, XCircle, Edit, Plus,
  Eye, RefreshCw, CheckCircle, AlertCircle, Briefcase, FileText,
  DollarSign, ChevronDown, ChevronUp, Lock, MoreVertical, AlertTriangle,
  Phone, MessageSquare, Users, Gift, CalendarClock, X, StickyNote,
  MessageCircle, Copy, ExternalLink, Loader2, Sparkles, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const CustomerDetail = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId } = useParams();
  const [customer, setCustomer] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Back navigation handler - returns to previous screen or falls back to CRM
  const handleBack = () => {
    // Check if there's navigation history (came from another page in the app)
    if (window.history.length > 2) {
      navigate(-1); // Go back preserving context (filters, scroll, etc.)
    } else {
      navigate('/crm'); // Fallback to CRM list if no history
    }
  };
  
  // Dialogs
  const [showScheduleVisitDialog, setShowScheduleVisitDialog] = useState(false);
  const [showFollowupDialog, setShowFollowupDialog] = useState(false);
  const [showLostDialog, setShowLostDialog] = useState(false);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [showStageDialog, setShowStageDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showAllNotesDialog, setShowAllNotesDialog] = useState(false);
  
  // Notes state
  const [noteContent, setNoteContent] = useState('');
  const [customerNotes, setCustomerNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  
  // WhatsApp Smart Note state
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  const [whatsAppMessage, setWhatsAppMessage] = useState('');
  const [whatsAppTone, setWhatsAppTone] = useState('Formal');
  const [whatsAppConverting, setWhatsAppConverting] = useState(false);
  const [lastSavedNote, setLastSavedNote] = useState('');
  
  // Visit lifecycle dialogs
  const [showCompleteVisitDialog, setShowCompleteVisitDialog] = useState(false);
  const [showCancelVisitDialog, setShowCancelVisitDialog] = useState(false);
  const [showRescheduleVisitDialog, setShowRescheduleVisitDialog] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  
  // Section collapse states
  const [expandedSections, setExpandedSections] = useState({
    requirements: true,
    visits: true,
    timeline: false,
  });
  
  // Schedule Visit form - simplified
  const [scheduleVisitForm, setScheduleVisitForm] = useState({
    project_id: '',
    project_name: '',
    visit_date: '',
    visit_time: '10:00',
    notes: '',
    auto_reminder: true,
    meeting_type: 'Site Visit',
    obm_purpose: '',
  });
  
  // Mark Complete form
  const [completeVisitForm, setCompleteVisitForm] = useState({
    project_id: '',
    project_name: '',
    completed_date: new Date().toISOString().split('T')[0],
    completed_time: new Date().toTimeString().slice(0, 5),
    remarks: '',
    next_followup_date: '',
    next_followup_time: '',
    lead_warmth: '',
  });
  
  // Cancel Visit form
  const [cancelVisitForm, setCancelVisitForm] = useState({
    cancel_reason: '',
    notes: '',
  });
  
  // Reschedule form
  const [rescheduleForm, setRescheduleForm] = useState({
    new_date: '',
    new_time: '10:00',
    notes: '',
  });
  
  // Legacy visit forms (for backward compatibility)
  const [visitForm, setVisitForm] = useState({
    project_id: '',
    project_name: '',
    visit_date: new Date().toISOString().split('T')[0],
    visit_time: new Date().toTimeString().slice(0, 5),
    remarks: '',
    status: 'COMPLETED',
    next_followup_date: '',
    next_followup_time: '',
  });
  
  // Cancel reasons
  const cancelReasons = [
    'Customer cancelled',
    'Customer not reachable',
    'Site issue',
    'Rep unavailable',
    'Other'
  ];
  
  const [followupForm, setFollowupForm] = useState({
    followup_type: 'Call follow-up',
    due_date: '',
    due_time: '10:00',
    notes: '',
  });
  
  const [lostForm, setLostForm] = useState({
    lost_reason: '',
    lost_remark: '',
  });
  
  const [bookingForm, setBookingForm] = useState({
    project_id: '',
    project_name: '',
    booking_date: new Date().toISOString().split('T')[0],
    booking_value: '',
    notes: '',
  });
  
  const [stageForm, setStageForm] = useState({
    stage: '',
    reason: '',
  });
  
  const [editForm, setEditForm] = useState({});
  const [noteForm, setNoteForm] = useState('');
  
  // Referral state
  const [referral, setReferral] = useState(null);
  const [showReferralDialog, setShowReferralDialog] = useState(false);
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralForm, setReferralForm] = useState({ name: '', phone: '', email: '' });
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    fetchData();
  }, [customerId]);

  const fetchData = async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const [customerRes, projectsRes, referralRes, notesRes] = await Promise.all([
        api.getCustomer(customerId),
        api.getProjects(),
        api.getReferral(customerId).catch(() => ({ data: { referral: null } })),
        api.getCustomerNotes(customerId, 50).catch(() => ({ data: { notes: [] } }))
      ]);
      if (!customerRes.data || !customerRes.data.customer_id) {
        setFetchError(true);
        setLoading(false);
        return;
      }
      setCustomer(customerRes.data);
      const projectsData = projectsRes.data?.projects || projectsRes.data || [];
      setProjects(Array.isArray(projectsData) ? projectsData : []);
      setEditForm(customerRes.data);
      setStageForm({ stage: customerRes.data.stage, reason: '' });
      setReferral(referralRes.data?.referral || null);
      setCustomerNotes(notesRes.data?.notes || []);
    } catch (error) {
      console.error('Failed to load customer:', error);
      setFetchError(true);
      if (error.response?.status === 403) {
        toast.error('Access denied. This customer belongs to another rep.');
      } else if (error.response?.status === 404) {
        toast.error('Customer not found');
      } else {
        toast.error('Failed to load customer');
      }
      // Don't navigate away - show error state instead
    } finally {
      setLoading(false);
    }
  };

  // Get Lead Health Status
  const getLeadHealth = () => {
    if (!customer) return null;
    
    const now = new Date();
    const lastActivity = customer.last_activity_at ? new Date(customer.last_activity_at) : null;
    const nextFollowup = customer.next_followup_datetime ? new Date(customer.next_followup_datetime) : null;
    
    const daysSinceActivity = lastActivity ? Math.floor((now - lastActivity) / (1000 * 60 * 60 * 24)) : 999;
    const isOverdue = nextFollowup && nextFollowup < now;
    const daysOverdue = isOverdue ? Math.floor((now - nextFollowup) / (1000 * 60 * 60 * 24)) : 0;
    
    if (isOverdue && daysOverdue > 0) {
      return {
        status: 'critical',
        color: 'text-[#5B3DF5]',
        bgColor: 'bg-red-50 border-red-100',
        icon: '🔴',
        text: `Follow-up overdue by ${daysOverdue} day${daysOverdue > 1 ? 's' : ''}`
      };
    }
    if (daysSinceActivity > 3) {
      return {
        status: 'warning',
        color: 'text-amber-600',
        bgColor: 'bg-amber-50 border-amber-100',
        icon: '🟡',
        text: `No activity in ${daysSinceActivity} days`
      };
    }
    return {
      status: 'healthy',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50 border-emerald-100',
      icon: '🟢',
      text: daysSinceActivity === 0 ? 'Active today' : daysSinceActivity === 1 ? 'Active yesterday' : `Active ${daysSinceActivity} days ago`
    };
  };

  // Get Next Action display
  const getNextAction = () => {
    if (!customer?.next_followup_datetime) return null;
    
    const followupDate = new Date(customer.next_followup_datetime);
    const now = new Date();
    const diffDays = Math.ceil((followupDate - now) / (1000 * 60 * 60 * 24));
    
    let dayText = '';
    if (diffDays < 0) dayText = `${Math.abs(diffDays)} day${Math.abs(diffDays) > 1 ? 's' : ''} overdue`;
    else if (diffDays === 0) dayText = 'today';
    else if (diffDays === 1) dayText = 'tomorrow';
    else dayText = `in ${diffDays} days`;
    
    const timeText = followupDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    
    return { dayText, timeText, isOverdue: diffDays < 0 };
  };

  // Format relative time
  const formatRelativeTime = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return ''; // Return empty string for invalid dates
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
      return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    } catch {
      return '';
    }
  };

  // Schedule Visit - only creates SCHEDULED visits
  const handleScheduleVisit = async () => {
    if (!scheduleVisitForm.project_id) { toast.error('Project is required'); return; }
    if (!scheduleVisitForm.visit_date || !scheduleVisitForm.visit_time) { 
      toast.error('Visit date and time are required'); return; 
    }
    
    try {
      await api.scheduleVisit(customerId, {
        project_id: scheduleVisitForm.project_id,
        project_name: scheduleVisitForm.project_name,
        visit_date: scheduleVisitForm.visit_date,
        visit_time: scheduleVisitForm.visit_time,
        notes: scheduleVisitForm.notes,
        auto_reminder: scheduleVisitForm.auto_reminder,
        meeting_type: scheduleVisitForm.meeting_type,
        obm_purpose: scheduleVisitForm.meeting_type === 'OBM' ? scheduleVisitForm.obm_purpose : undefined,
      });
      
      toast.success(`${scheduleVisitForm.meeting_type} scheduled!`);
      setShowScheduleVisitDialog(false);
      setScheduleVisitForm({ project_id: '', project_name: '', visit_date: '', visit_time: '10:00', notes: '', auto_reminder: true, meeting_type: 'Site Visit', obm_purpose: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule');
    }
  };

  // Mark Visit as Completed
  const handleMarkVisitCompleted = async () => {
    if (!selectedVisit) return;
    
    // Check if token exists before making request
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Session expired. Please login again.');
      window.location.href = '/login';
      return;
    }
    
    try {
      await api.markVisitCompleted(selectedVisit.event_id, {
        project_id: completeVisitForm.project_id || selectedVisit.project_id,
        project_name: completeVisitForm.project_name || selectedVisit.project_name,
        completed_date: completeVisitForm.completed_date,
        completed_time: completeVisitForm.completed_time,
        remarks: completeVisitForm.remarks,
        next_followup_date: completeVisitForm.next_followup_date || undefined,
        next_followup_time: completeVisitForm.next_followup_time || undefined,
        lead_warmth: completeVisitForm.lead_warmth || undefined,
      });
      
      toast.success('Visit marked as completed');
      setShowCompleteVisitDialog(false);
      setSelectedVisit(null);
      setCompleteVisitForm({
        project_id: '',
        project_name: '',
        completed_date: new Date().toISOString().split('T')[0],
        completed_time: new Date().toTimeString().slice(0, 5),
        remarks: '',
        next_followup_date: '',
        next_followup_time: '',
        lead_warmth: '',
      });
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to mark visit as completed';
      // If auth error, prompt re-login
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error(errorMsg);
      }
    }
  };

  // Cancel Visit
  const handleCancelVisit = async () => {
    if (!selectedVisit) return;
    if (!cancelVisitForm.cancel_reason) {
      toast.error('Cancel reason is required'); return;
    }
    
    try {
      await api.cancelVisit(selectedVisit.event_id, {
        cancel_reason: cancelVisitForm.cancel_reason,
        notes: cancelVisitForm.notes,
      });
      
      toast.success('Visit cancelled');
      setShowCancelVisitDialog(false);
      setSelectedVisit(null);
      setCancelVisitForm({ cancel_reason: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel visit');
    }
  };

  // Reschedule Visit
  const handleRescheduleVisit = async () => {
    if (!selectedVisit) return;
    if (!rescheduleForm.new_date || !rescheduleForm.new_time) {
      toast.error('New date and time are required'); return;
    }
    
    try {
      await api.rescheduleVisit(selectedVisit.event_id, {
        new_date: rescheduleForm.new_date,
        new_time: rescheduleForm.new_time,
        notes: rescheduleForm.notes,
      });
      
      toast.success('Visit rescheduled');
      setShowRescheduleVisitDialog(false);
      setSelectedVisit(null);
      setRescheduleForm({ new_date: '', new_time: '10:00', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reschedule visit');
    }
  };

  // Open complete visit dialog
  const openCompleteVisitDialog = (visit) => {
    setSelectedVisit(visit);
    
    // Pre-populate with scheduled visit date/time (user can edit if actual completion time differs)
    let completedDate = new Date().toISOString().split('T')[0];
    let completedTime = new Date().toTimeString().slice(0, 5);
    
    // If visit has a scheduled datetime, use that as default (editable)
    if (visit.visit_datetime) {
      try {
        const visitDate = new Date(visit.visit_datetime);
        if (!isNaN(visitDate.getTime())) {
          completedDate = visitDate.toISOString().split('T')[0];
          completedTime = visitDate.toTimeString().slice(0, 5);
        }
      } catch (e) {
        // Fall back to current time if parsing fails
      }
    }
    
    setCompleteVisitForm({
      project_id: visit.project_id,
      project_name: visit.project_name,
      completed_date: completedDate,
      completed_time: completedTime,
      remarks: visit.remarks || '',
      next_followup_date: '',
      next_followup_time: '',
    });
    setShowCompleteVisitDialog(true);
  };

  // Open cancel visit dialog
  const openCancelVisitDialog = (visit) => {
    setSelectedVisit(visit);
    setCancelVisitForm({ cancel_reason: '', notes: '' });
    setShowCancelVisitDialog(true);
  };

  // Open reschedule dialog
  const openRescheduleDialog = (visit) => {
    setSelectedVisit(visit);
    setRescheduleForm({ 
      new_date: visit.visit_datetime?.split('T')[0] || '', 
      new_time: visit.visit_datetime?.split('T')[1]?.slice(0, 5) || '10:00', 
      notes: '' 
    });
    setShowRescheduleVisitDialog(true);
  };

  // Handle add referral lead
  const handleAddReferral = async () => {
    if (!referralForm.name || !referralForm.phone) {
      toast.error('Name and phone are required');
      return;
    }
    if (referralForm.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    try {
      await api.addReferralLead(customerId, {
        name: referralForm.name,
        phone: referralForm.phone,
        email: referralForm.email || undefined,
      });
      toast.success('Referral added successfully');
      setShowReferralDialog(false);
      setReferralForm({ name: '', phone: '', email: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add referral');
    }
  };

  const handleCreateFollowup = async () => {
    if (!followupForm.due_date) { toast.error('Due date is required'); return; }
    if (!followupForm.notes.trim()) { toast.error('Notes are required'); return; }
    
    try {
      const dueDatetime = `${followupForm.due_date}T${followupForm.due_time}:00`;
      await api.createFollowup(customerId, { followup_type: followupForm.followup_type, due_datetime: dueDatetime, notes: followupForm.notes });
      toast.success('Follow-up scheduled');
      setShowFollowupDialog(false);
      setFollowupForm({ followup_type: 'Call follow-up', due_date: '', due_time: '10:00', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create follow-up');
    }
  };

  const handleMarkLost = async () => {
    if (!lostForm.lost_reason) { toast.error('Please select a reason'); return; }
    if (!lostForm.lost_remark.trim()) { toast.error('Final remark is mandatory'); return; }
    
    try {
      await api.markCustomerLost(customerId, lostForm);
      toast.success('Customer marked as Lost');
      setShowLostDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as lost');
    }
  };

  const handleCreateBooking = async () => {
    if (!bookingForm.project_id || !bookingForm.booking_value) { toast.error('Project and value are required'); return; }
    
    try {
      // Convert lacs to actual value (1 lac = 100000)
      const bookingValueRupees = parseFloat(bookingForm.booking_value) * 100000;
      
      await api.createBookingFromCRM(customerId, {
        project_id: bookingForm.project_id,
        project_name: bookingForm.project_name,
        booking_date: bookingForm.booking_date,
        booking_value: bookingValueRupees,
        notes: bookingForm.notes,
      });
      toast.success('Booking created');
      setShowBookingDialog(false);
      setBookingForm({ project_id: '', project_name: '', booking_date: new Date().toISOString().split('T')[0], booking_value: '', notes: '' });
      fetchData();
    } catch (error) {
      // Handle Pydantic validation errors (array of error objects) or string errors
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg || 'Validation error');
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to create booking');
      }
    }
  };

  const handleChangeStage = async () => {
    if (stageForm.stage !== customer.stage && !stageForm.reason.trim()) { toast.error('Please provide a reason'); return; }
    
    try {
      await api.updateCustomer(customerId, { stage: stageForm.stage, stage_override_reason: stageForm.reason });
      toast.success('Stage updated');
      setShowStageDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    }
  };

  const handleUpdateCustomer = async () => {
    try {
      await api.updateCustomer(customerId, {
        customer_name: editForm.customer_name,
        budget_min_lacs: editForm.budget_min_lacs ? parseFloat(editForm.budget_min_lacs) : null,
        budget_max_lacs: editForm.budget_max_lacs ? parseFloat(editForm.budget_max_lacs) : null,
        preferred_locations: editForm.preferred_locations,
        configuration: editForm.configuration,
        purpose: editForm.purpose,
        notes: editForm.notes,
      });
      toast.success('Customer updated');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim()) {
      toast.error('Note content is required');
      return;
    }
    try {
      setNotesLoading(true);
      const savedNote = noteContent.trim();
      await api.addCustomerNote(customerId, { content: savedNote });
      toast.success('Note added');
      setShowNoteDialog(false);
      setNoteContent('');
      setLastSavedNote(savedNote);
      // Refresh notes
      const notesRes = await api.getCustomerNotes(customerId, 50);
      setCustomerNotes(notesRes.data?.notes || []);
      fetchData();
      
      // Show WhatsApp conversion option if note is substantial enough
      if (savedNote.split(' ').length >= 5) {
        setShowWhatsAppDialog(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setNotesLoading(false);
    }
  };

  const handleConvertToWhatsApp = async () => {
    if (!lastSavedNote) {
      toast.error('No note to convert');
      return;
    }
    try {
      setWhatsAppConverting(true);
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const repName = currentUser.full_name || currentUser.rep_name || 'Your Representative';
      
      const response = await api.convertNoteToWhatsApp({
        customer_name: customer?.customer_name || 'Customer',
        rep_name: repName,
        note_content: lastSavedNote,
        tone_type: whatsAppTone
      });
      
      if (response.data.success) {
        setWhatsAppMessage(response.data.whatsapp_message);
      } else {
        toast.error(response.data.message || 'Could not convert note');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert note');
    } finally {
      setWhatsAppConverting(false);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(whatsAppMessage);
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleShareViaWhatsApp = () => {
    const phone = customer?.customer_phone?.replace(/\D/g, '') || '';
    const encodedMessage = encodeURIComponent(whatsAppMessage);
    const whatsAppUrl = phone 
      ? `https://wa.me/91${phone}?text=${encodedMessage}`
      : `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsAppUrl, '_blank');
  };

  const getStageIcon = (stage) => {
    switch (stage) {
      case 'Hot': return <Flame size={12} className="text-white" />;
      case 'Warm': return <ThermometerSun size={12} className="text-white" />;
      case 'Cold': return <Snowflake size={12} className="text-white" />;
      case 'Won': return <Trophy size={12} className="text-white" />;
      case 'Lost': return <XCircle size={12} className="text-slate-400" />;
      default: return null;
    }
  };

  const getStageBadgeClass = (stage) => {
    const config = {
      Hot: 'bg-[#5B3DF5] text-white',
      Warm: 'bg-amber-500 text-white',
      Cold: 'bg-slate-500 text-white',
      Won: 'bg-emerald-500 text-white',
      Lost: 'bg-slate-200 text-slate-500',
    };
    return config[stage] || 'bg-slate-100 text-slate-700';
  };

  const formatBudget = (minLacs, maxLacs) => {
    if (!minLacs && !maxLacs) return 'Not set';
    const min = minLacs ? `₹${minLacs}L` : '';
    const max = maxLacs ? `₹${maxLacs}L` : '';
    if (min && max) return `${min}–${max}`;
    return min || max;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return ''; // Return empty string for invalid dates
      return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) + 
             ', ' + date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const lostReasons = ['Bought Elsewhere', 'Not Reachable/No Response', 'Not Interested/Dropped Plan', 'Budget Mismatch', 'Product/Location Mismatch', 'Delayed(On Hold)', 'Other'];
  const followupTypes = ['Call follow-up', 'Meeting', 'WhatsApp follow-up', 'Email follow-up'];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-[#EF4444]"></div>
      </div>
    );
  }

  // Error state - customer not found, access denied, or fetch failed
  if (!customer || fetchError) {
    return (
      <div className="min-h-screen bg-[#F7F8FA]">
        <BreadcrumbNav 
          items={[
            { label: 'CRM', path: '/crm' },
            { label: 'Error' }
          ]}
          fallbackPath="/crm"
        />
        <div className="max-w-lg mx-auto mt-20 p-6 bg-white rounded-xl shadow-lg text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Customer Not Found</h2>
          <p className="text-slate-500 mb-6">
            The customer you're looking for doesn't exist or you don't have permission to view it.
          </p>
          <Button 
            onClick={() => navigate('/crm')}
            className="bg-[#EF4444] hover:bg-red-600 text-white"
          >
            ← Back to CRM
          </Button>
        </div>
      </div>
    );
  }

  const isReadOnly = customer.is_read_only || customer.stage === 'Lost';
  const leadHealth = getLeadHealth();
  const nextAction = getNextAction();

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-24">
      {/* Breadcrumb Navigation */}
      <BreadcrumbNav 
        items={[
          { label: 'CRM', path: '/crm' },
          { label: customer.customer_name }
        ]}
        fallbackPath="/crm"
      />
      
      {/* Header - Customer Info */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 sticky top-[52px] z-10 shadow-sm">
        <div className="flex items-center gap-3 max-w-4xl mx-auto">
          <UserAvatar name={customer.customer_name} size="md" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-base font-bold text-[#111827] truncate">{customer.customer_name}</h1>
              <button onClick={() => !isReadOnly && setShowStageDialog(true)} disabled={isReadOnly}>
                <Badge className={`${getStageBadgeClass(customer.stage)} flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 ${isReadOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
                  {getStageIcon(customer.stage)}
                  {customer.stage}
                </Badge>
              </button>
              {/* Inline System Suggestion */}
              {!isReadOnly && customer.system_suggested_stage && !customer.stage_confirmed && customer.system_suggested_stage !== customer.stage && (
                <button 
                  onClick={() => setShowStageDialog(true)}
                  className="text-[10px] text-amber-600 hover:text-amber-700 flex items-center gap-0.5"
                >
                  <Flame size={10} />
                  <span className="underline">Suggests {customer.system_suggested_stage}</span>
                </button>
              )}
              {/* Retargeting Lead Tag - differentiate between Lost Lead Recovery and VDNB */}
              {customer.is_retargeting_lead && (
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1 cursor-help border ${
                        customer.retargeting_type === 'VDNB'
                          ? 'bg-purple-100 text-purple-700 border-purple-200'
                          : 'bg-orange-100 text-orange-700 border-orange-200'
                      }`}>
                        <RotateCcw size={10} />
                        {customer.retargeting_type === 'VDNB' ? 'VDNB' : 'Retargeting'}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="bg-slate-800 text-white p-2.5 max-w-[220px]">
                      <div className="text-[11px] space-y-1.5">
                        <p className={`font-semibold text-xs ${customer.retargeting_type === 'VDNB' ? 'text-purple-300' : 'text-orange-300'}`}>
                          {customer.retargeting_type === 'VDNB' ? 'VDNB Recovery' : 'Lost Lead Recovery'}
                        </p>
                        {customer.original_rep_name && (
                          <p>Original Rep: <span className="font-medium">{customer.original_rep_name}</span></p>
                        )}
                        {(customer.reassigned_from_lost_by_name || customer.reassigned_from_vdnb_by_name) && (
                          <p>Reassigned By: <span className="font-medium">{customer.reassigned_from_lost_by_name || customer.reassigned_from_vdnb_by_name}</span></p>
                        )}
                        {(customer.reassigned_from_lost_at || customer.reassigned_from_vdnb_at) && (
                          <p>Date: <span className="font-medium">{new Date(customer.reassigned_from_lost_at || customer.reassigned_from_vdnb_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span></p>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <p className="text-xs text-slate-500 truncate">{customer.customer_email}</p>
          </div>
          {!isReadOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2 hover:bg-slate-100 rounded-xl">
                  <MoreVertical size={18} className="text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShowEditDialog(true)}>
                  <Edit size={14} className="mr-2" /> Edit Customer
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowLostDialog(true)} className="text-[#5B3DF5]">
                  <XCircle size={14} className="mr-2" /> Mark Lost
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        
        {/* Read-only indicator */}
        {isReadOnly && (
          <div className="mt-2 max-w-4xl mx-auto">
            <div className="flex items-center gap-2 text-xs text-slate-600 bg-slate-100 px-3 py-2 rounded-xl">
              <Lock size={12} />
              <span>Read-only. Contact admin to revive.</span>
            </div>
          </div>
        )}
      </div>

      <div className="p-4 space-y-3 max-w-4xl mx-auto">
        {/* Next Action Card - Critical */}
        {!isReadOnly && (
          <Card className={`p-4 rounded-2xl shadow-sm ${nextAction ? (nextAction.isOverdue ? 'bg-red-50 border border-red-200' : 'bg-white') : 'bg-amber-50 border border-amber-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide mb-1">Next Action</p>
                {nextAction ? (
                  <div>
                    <p className={`text-sm font-bold ${nextAction.isOverdue ? 'text-[#5B3DF5]' : 'text-[#111827]'} mb-1`}>
                      <Phone size={14} className="inline mr-1" />
                      Call client {nextAction.dayText} at {nextAction.timeText}
                    </p>
                    <p className="text-xs text-blue-600 flex items-center gap-1">
                      <span>📌</span> Synced with My Tasks
                    </p>
                  </div>
                ) : (
                  <p className="text-sm font-bold text-amber-600 flex items-center gap-1">
                    <AlertTriangle size={14} />
                    No follow-up scheduled
                  </p>
                )}
              </div>
              {!nextAction && (
                <Button size="sm" onClick={() => setShowFollowupDialog(true)} className="bg-amber-500 hover:bg-amber-600 text-white h-8 text-xs">
                  Schedule
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Lead Health Strip */}
        {!isReadOnly && leadHealth && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium border ${leadHealth.bgColor} ${leadHealth.color}`}>
            <span>{leadHealth.icon}</span>
            <span>Lead Health: {leadHealth.text}</span>
          </div>
        )}

        {/* Quick Actions - Mobile: 2x2 grid, Desktop: original layout */}
        {!isReadOnly && (
          <>
            {/* Mobile: 2x3 Grid */}
            <div className="grid grid-cols-2 gap-2 md:hidden">
              <Button 
                data-testid="add-visit-button-mobile"
                onClick={() => setShowScheduleVisitDialog(true)}
                className="bg-[#5B3DF5] hover:bg-[#4A2FE0] text-white rounded-xl h-11 text-sm font-medium"
              >
                <Eye size={16} className="mr-1.5 flex-shrink-0" />
                <span className="truncate">Schedule Visit</span>
              </Button>
              <Button 
                onClick={() => navigate(`/bookings/new?customer_id=${customerId}&customer_email=${encodeURIComponent(customer.customer_email || '')}&customer_name=${encodeURIComponent(customer.customer_name || '')}`)}
                variant="outline"
                className="rounded-xl h-11 text-sm font-medium border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                disabled={customer.linked_booking_id}
                data-testid="add-booking-btn-mobile"
              >
                <DollarSign size={16} className="mr-1.5 flex-shrink-0" />
                <span className="truncate">Add Booking</span>
              </Button>
              <Button 
                onClick={() => setShowFollowupDialog(true)}
                variant="outline"
                className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl h-11 text-sm font-medium"
                data-testid="add-followup-btn-mobile"
              >
                <Calendar size={16} className="mr-1.5 flex-shrink-0" />
                <span className="truncate">Add Follow-up</span>
              </Button>
              <Button 
                onClick={() => setShowNoteDialog(true)}
                variant="outline"
                className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl h-11 text-sm font-medium"
                data-testid="add-notes-btn-mobile"
              >
                <StickyNote size={16} className="mr-1.5 flex-shrink-0" />
                <span className="truncate">Add Notes</span>
              </Button>
              <Button 
                onClick={() => navigate(`/saira?lead_id=${customerId}&lead_name=${encodeURIComponent(customer.customer_name || '')}&project=${encodeURIComponent(customer.first_project_name || '')}`)}
                variant="outline"
                className="col-span-2 border-purple-200 text-purple-700 hover:bg-purple-50 rounded-xl h-11 text-sm font-medium"
                data-testid="ask-saira-btn-mobile"
              >
                <Sparkles size={16} className="mr-1.5 flex-shrink-0" />
                <span className="truncate">Ask SAIRA for Help</span>
              </Button>
            </div>
            
            {/* Desktop: Original layout */}
            <div className="hidden md:block space-y-2">
              <Button
                data-testid="add-visit-button"
                onClick={() => setShowScheduleVisitDialog(true)}
                className="w-full bg-[#5B3DF5] hover:bg-[#4A2FE0] h-10 font-bold rounded-xl"
              >
                <Eye size={16} className="mr-2" />
                Schedule Visit
              </Button>
              <div className="grid grid-cols-4 gap-2">
                <Button onClick={() => setShowFollowupDialog(true)} variant="outline" className="h-9 text-sm rounded-xl">
                  <Calendar size={14} className="mr-1" />
                  Follow-up
                </Button>
                <Button
                  onClick={() => navigate(`/bookings/new?customer_id=${customerId}&customer_email=${encodeURIComponent(customer.customer_email || '')}&customer_name=${encodeURIComponent(customer.customer_name || '')}`)}
                  variant="outline"
                  className="h-9 text-sm rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  disabled={customer.linked_booking_id}
                >
                  <DollarSign size={14} className="mr-1" />
                  Booking
                </Button>
                <Button 
                  onClick={() => setShowNoteDialog(true)} 
                  variant="outline" 
                  className="h-9 text-sm rounded-xl"
                >
                  <StickyNote size={14} className="mr-1" />
                  Notes
                </Button>
                <Button 
                  onClick={() => navigate(`/saira?lead_id=${customerId}&lead_name=${encodeURIComponent(customer.customer_name || '')}&project=${encodeURIComponent(customer.first_project_name || '')}`)}
                  variant="outline" 
                  className="h-9 text-sm rounded-xl border-purple-200 text-purple-700 hover:bg-purple-50"
                  data-testid="ask-saira-btn"
                >
                  <Sparkles size={14} className="mr-1" />
                  SAIRA
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Metrics Row - Refined */}
        <Card className="p-3 bg-white shadow-sm rounded-2xl">
          <div className="grid grid-cols-4 gap-2">
            <div className="text-center">
              <Eye size={16} className="mx-auto text-blue-500 mb-1" />
              <p className="text-xl font-bold text-[#111827]">{customer.total_visit_events || 0}</p>
              <p className="text-[10px] text-slate-400 uppercase">Visits</p>
            </div>
            <div className="text-center">
              <Building2 size={16} className="mx-auto text-purple-500 mb-1" />
              <p className="text-xl font-bold text-[#111827]">{customer.unique_projects || 0}</p>
              <p className="text-[10px] text-slate-400 uppercase">Projects</p>
            </div>
            <div className="text-center">
              <RefreshCw size={16} className="mx-auto text-emerald-500 mb-1" />
              <p className="text-xl font-bold text-[#111827]">{customer.revisit_count || 0}</p>
              <p className="text-[10px] text-slate-400 uppercase">Revisits</p>
            </div>
            <div className="text-center">
              <Clock size={16} className="mx-auto text-amber-500 mb-1" />
              <p className="text-sm font-bold text-[#111827]">{formatRelativeTime(customer.last_visit_datetime)}</p>
              <p className="text-[10px] text-slate-400 uppercase">Last Visit</p>
            </div>
          </div>
        </Card>

        {/* Linked Booking - Fixed colors */}
        {customer.linked_booking && (
          <Card className={`p-3 rounded-2xl border ${
            customer.linked_booking.status === 'Rejected' 
              ? 'bg-red-50 border-red-200' 
              : customer.linked_booking.status === 'Approved'
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-amber-50 border-amber-200'
          }`}>
            <div className="flex items-center gap-3">
              <DollarSign size={18} className={
                customer.linked_booking.status === 'Rejected' ? 'text-[#5B3DF5]' :
                customer.linked_booking.status === 'Approved' ? 'text-emerald-600' : 'text-amber-600'
              } />
              <div className="flex-1">
                <p className="font-semibold text-sm text-[#111827]">Booking: {customer.linked_booking.project_name}</p>
                <p className="text-xs text-slate-600">
                  ₹{(customer.linked_booking.booking_value / 100000).toFixed(0)}L • 
                  Status: <span className={`font-bold ${
                    customer.linked_booking.status === 'Rejected' ? 'text-[#5B3DF5]' :
                    customer.linked_booking.status === 'Approved' ? 'text-emerald-600' : 'text-amber-600'
                  }`}>
                    {customer.linked_booking.status === 'Rejected' ? '❌' : customer.linked_booking.status === 'Approved' ? '✅' : '⏳'} {customer.linked_booking.status}
                  </span>
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Requirements Section - Compact Grid */}
        <Card className="bg-white shadow-sm rounded-2xl overflow-hidden">
          <button onClick={() => toggleSection('requirements')} className="w-full p-3 flex items-center justify-between hover:bg-slate-50">
            <div className="flex items-center gap-2">
              <Home size={16} className="text-[#5B3DF5]" />
              <h3 className="font-bold text-sm text-[#111827]">Requirements</h3>
            </div>
            {expandedSections.requirements ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          
          {expandedSections.requirements && (
            <div className="px-3 pb-3 grid grid-cols-2 gap-2 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-400">Budget</span>
                <span className="font-semibold text-[#111827]">{formatBudget(customer.budget_min_lacs, customer.budget_max_lacs)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Config</span>
                <span className="font-semibold text-[#111827]">{customer.configuration || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Purpose</span>
                <span className="font-semibold text-[#111827]">{customer.purpose || 'End-use'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Locations</span>
                <span className="font-semibold text-[#111827] truncate max-w-[100px]">{customer.preferred_locations?.join(', ') || '—'}</span>
              </div>
            </div>
          )}
        </Card>

        {/* Referral Section */}
        <Card className="bg-white shadow-sm rounded-2xl overflow-hidden">
          <div className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users size={16} className="text-purple-500" />
              <h3 className="font-bold text-sm text-[#111827]">Referral</h3>
              {referral && (
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                  referral.status === 'BENEFIT_ELIGIBLE' ? 'bg-emerald-100 text-emerald-700' :
                  referral.status === 'AGREEMENT_DONE' ? 'bg-blue-100 text-blue-700' :
                  referral.status === 'BOOKING_VERIFIED' ? 'bg-indigo-100 text-indigo-700' :
                  referral.status === 'BOOKING_CREATED' ? 'bg-cyan-100 text-cyan-700' :
                  referral.status === 'VISIT_DONE' ? 'bg-amber-100 text-amber-700' :
                  referral.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
                  'bg-slate-100 text-slate-600'
                }`}>
                  {referral.status?.replace(/_/g, ' ')}
                </span>
              )}
            </div>
            {!referral && customer?.stage !== 'Lost' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowReferralDialog(true)}
                className="h-7 text-xs rounded-lg"
              >
                <Plus size={12} className="mr-1" />
                Add Referral
              </Button>
            )}
          </div>
          
          {referral ? (
            <div className="px-3 pb-3 space-y-2">
              <div className="p-2.5 bg-purple-50 rounded-xl space-y-2">
                {/* Referrer Info */}
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 text-xs font-bold">
                    {referral.referrer_type === 'employee' ? '👤' : (referral.referrer_name?.charAt(0) || 'C')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-purple-900 truncate">
                      {referral.referrer_type === 'employee' ? referral.referrer_employee_email : referral.referrer_name || 'Customer Referrer'}
                    </p>
                    <p className="text-[10px] text-purple-600 capitalize">{referral.referrer_type} Referral</p>
                  </div>
                </div>
                
                {/* Status Flow */}
                <div className="grid grid-cols-3 gap-1 text-center pt-2 border-t border-purple-100">
                  <div>
                    <p className="text-[10px] text-purple-400">Benefit Rate</p>
                    <p className="text-sm font-bold text-purple-700">{referral.benefit_rate || 0.5}%</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-purple-400">Status</p>
                    <p className="text-xs font-semibold text-purple-700">{referral.status?.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-purple-400">Payout</p>
                    <p className="text-xs font-semibold text-purple-700">
                      {referral.payout_status || (referral.status === 'BENEFIT_ELIGIBLE' ? 'Pending' : '—')}
                    </p>
                  </div>
                </div>
                
                {/* Benefit Eligible Indicator */}
                {referral.status === 'BENEFIT_ELIGIBLE' && (
                  <div className="flex items-center gap-2 p-2 bg-emerald-100 rounded-lg mt-2">
                    <Gift size={14} className="text-emerald-600" />
                    <p className="text-xs text-emerald-700 font-medium">
                      Eligible for payout {referral.payout_amount ? `(₹${referral.payout_amount.toLocaleString()})` : ''}
                    </p>
                  </div>
                )}
                
                {/* Admin Actions */}
                {(user?.role === 'Admin' || user?.role === 'super_admin') && referral.status !== 'REJECTED' && (
                  <div className="flex gap-2 pt-2 border-t border-purple-100">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowReferralDialog(true)}
                      className="flex-1 h-7 text-xs text-purple-700 border-purple-200"
                    >
                      <Edit size={10} className="mr-1" />
                      Change Referrer
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="px-3 pb-3">
              <div className="p-4 bg-slate-50 rounded-xl text-center">
                <p className="text-xs text-slate-500">No referral attribution</p>
                <p className="text-[10px] text-slate-400 mt-1">This customer was not referred</p>
              </div>
            </div>
          )}
        </Card>

        {/* Upcoming Visits Section */}
        {!isReadOnly && (
          <Card className="bg-blue-50 border border-blue-200 shadow-sm rounded-2xl overflow-hidden">
            <div className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <CalendarClock size={16} className="text-blue-600" />
                <h3 className="font-bold text-sm text-blue-800">Upcoming Visits</h3>
                {customer.scheduled_visits?.length > 0 && (
                  <span className="text-[10px] bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full font-medium">{customer.scheduled_visits.length}</span>
                )}
              </div>
              {customer.scheduled_visits?.length > 0 ? (
                <div className="space-y-2">
                  {customer.scheduled_visits.map((visit) => (
                    <div key={visit.event_id} className="p-2.5 bg-white rounded-xl border border-blue-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-sm text-[#111827]">{visit.project_name}</p>
                          <p className="text-xs text-slate-500">{formatDateTime(visit.visit_datetime)}</p>
                          {visit.remarks && <p className="text-[10px] text-slate-400 mt-1">{visit.remarks}</p>}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            onClick={() => openCompleteVisitDialog(visit)}
                            className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                          >
                            <CheckCircle size={12} className="mr-1" />
                            Complete
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="outline" className="h-7 w-7 p-0">
                                <MoreVertical size={14} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openCancelVisitDialog(visit)}>
                                <X size={14} className="mr-2" /> Cancel Visit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openRescheduleDialog(visit)}>
                                <RefreshCw size={14} className="mr-2" /> Reschedule
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-3 bg-white rounded-xl text-center">
                  <p className="text-xs text-slate-500">No visit scheduled</p>
                  <Button
                    size="sm"
                    variant="link"
                    onClick={() => setShowScheduleVisitDialog(true)}
                    className="text-blue-600 text-xs mt-1 h-6"
                  >
                    Schedule a visit →
                  </Button>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Project-wise Visits - Improved */}
        <Card className="bg-white shadow-sm rounded-2xl overflow-hidden">
          <button onClick={() => toggleSection('visits')} className="w-full p-3 flex items-center justify-between hover:bg-slate-50">
            <div className="flex items-center gap-2">
              <Building2 size={16} className="text-[#5B3DF5]" />
              <h3 className="font-bold text-sm text-[#111827]">Visit History</h3>
              <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{customer.project_wise_visits?.length || 0} projects</span>
            </div>
            {expandedSections.visits ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          
          {expandedSections.visits && (
            <div className="px-3 pb-3 space-y-2">
              {customer.project_wise_visits?.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">No completed visits yet</p>
              ) : (
                customer.project_wise_visits?.map((pv, idx) => (
                  <div key={pv.project_id} className={`p-2.5 rounded-xl ${idx === 0 ? 'bg-blue-50 border border-blue-100' : 'bg-slate-50'}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="font-semibold text-sm text-[#111827]">{pv.project_name}</p>
                      <div className="flex gap-1">
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                          {pv.visits.length} visit{pv.visits.length !== 1 ? 's' : ''}
                        </span>
                        {pv.revisit_count > 0 && (
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                            {pv.revisit_count} revisit{pv.revisit_count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      {pv.visits.slice(0, 3).map((visit) => (
                        <div key={visit.event_id} className="flex items-start gap-2 text-xs">
                          <div className={`w-1.5 h-1.5 rounded-full mt-1.5 ${
                            visit.status === 'CANCELLED' ? 'bg-red-400' :
                            visit.event_type === 'Revisit' ? 'bg-emerald-500' : 'bg-blue-500'
                          }`} />
                          <div className="flex-1">
                            <span className="text-slate-500">{formatRelativeTime(visit.visit_datetime)}</span>
                            {visit.event_type === 'Revisit' && <span className="ml-1 text-emerald-600 font-medium">(Revisit)</span>}
                            {visit.status === 'CANCELLED' && <span className="ml-1 text-red-500 font-medium">(Cancelled)</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </Card>

        {/* Notes Card */}
        <Card className="bg-white shadow-sm rounded-2xl overflow-hidden">
          <div className="p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <StickyNote size={16} className="text-amber-600" />
                <h3 className="font-bold text-sm text-[#111827]">Notes</h3>
                <span className="text-[10px] text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full font-medium">
                  {customerNotes.length}
                </span>
              </div>
              {customerNotes.length > 3 && (
                <button
                  onClick={() => setShowAllNotesDialog(true)}
                  className="text-xs text-[#5B3DF5] hover:text-[#4A2FE0] font-medium"
                >
                  View all
                </button>
              )}
            </div>
            
            {customerNotes.length > 0 ? (
              <div className="space-y-2">
                {customerNotes.slice(0, 3).map((note, idx) => (
                  <div key={note.note_id || idx} className="p-2.5 bg-amber-50/50 rounded-xl border border-amber-100">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{note.content}</p>
                    <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-400">
                      <span>{note.created_by_name}</span>
                      <span>•</span>
                      <span>{formatDateTime(note.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 bg-slate-50 rounded-xl">
                <StickyNote size={20} className="mx-auto text-slate-300 mb-1" />
                <p className="text-xs text-slate-500">No notes yet</p>
                <button
                  onClick={() => setShowNoteDialog(true)}
                  className="text-xs text-[#5B3DF5] hover:text-[#4A2FE0] font-medium mt-1"
                >
                  Add your first note
                </button>
              </div>
            )}
          </div>
        </Card>

        {/* Timeline - Improved hierarchy */}
        <Card className="bg-white shadow-sm rounded-2xl overflow-hidden">
          <button onClick={() => toggleSection('timeline')} className="w-full p-3 flex items-center justify-between hover:bg-slate-50">
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-[#5B3DF5]" />
              <h3 className="font-bold text-sm text-[#111827]">Timeline</h3>
              <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{customer.timeline?.length || 0}</span>
            </div>
            {expandedSections.timeline ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          
          {expandedSections.timeline && (
            <div className="px-3 pb-3 space-y-2">
              {customer.timeline?.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">No activity yet</p>
              ) : (
                customer.timeline?.slice(0, 15).map((event) => (
                  <div key={event.event_id} className="flex items-start gap-2 text-xs">
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5
                      ${event.event_type === 'visit' ? 'bg-blue-100' : ''}
                      ${event.event_type === 'revisit' ? 'bg-emerald-100' : ''}
                      ${event.event_type === 'followup_created' || event.event_type === 'followup_done' ? 'bg-amber-100' : ''}
                      ${event.event_type === 'stage_change' ? 'bg-purple-100' : ''}
                      ${event.event_type === 'booking_created' ? 'bg-green-100' : ''}
                      ${event.event_type === 'note' ? 'bg-slate-100' : ''}
                    `}>
                      {event.event_type === 'visit' && <Eye size={12} className="text-blue-600" />}
                      {event.event_type === 'revisit' && <RefreshCw size={12} className="text-emerald-600" />}
                      {(event.event_type === 'followup_created' || event.event_type === 'followup_done') && <Calendar size={12} className="text-amber-600" />}
                      {event.event_type === 'stage_change' && <AlertCircle size={12} className="text-purple-600" />}
                      {event.event_type === 'booking_created' && <DollarSign size={12} className="text-green-600" />}
                      {event.event_type === 'note' && <FileText size={12} className="text-slate-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#111827]">{event.title}</p>
                      {event.description && <p className="text-slate-500 truncate">{event.description}</p>}
                      <p className="text-slate-300 text-[10px] mt-0.5">{formatDateTime(event.event_datetime)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Sticky Bottom Action Bar */}
      {!isReadOnly && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-3 shadow-lg">
          <div className="max-w-4xl mx-auto flex gap-2">
            <Button onClick={() => navigate(`/bookings/new?customer_id=${customerId}&customer_email=${encodeURIComponent(customer.customer_email || '')}&customer_name=${encodeURIComponent(customer.customer_name || '')}`)} className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0] h-11 font-bold rounded-xl" disabled={customer.linked_booking_id}>
              <DollarSign size={16} className="mr-1" />
              Create Booking
            </Button>
            <Button onClick={() => setShowNoteDialog(true)} variant="outline" className="h-11 px-4 rounded-xl">
              <MessageSquare size={16} />
            </Button>
          </div>
        </div>
      )}

      {/* Dialogs - New Visit Lifecycle Dialogs */}
      
      {/* Schedule Visit Dialog */}
      <Dialog open={showScheduleVisitDialog} onOpenChange={setShowScheduleVisitDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Schedule Meeting</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            {/* Meeting Type Selection */}
            <div>
              <Label className="text-xs">Meeting Type *</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button type="button" onClick={() => setScheduleVisitForm({...scheduleVisitForm, meeting_type: 'Site Visit', obm_purpose: ''})} className={`p-2.5 rounded-xl text-xs font-medium border transition-all flex items-center justify-center gap-2 ${scheduleVisitForm.meeting_type === 'Site Visit' ? 'border-purple-400 bg-purple-50 text-purple-700' : 'border-slate-200 bg-white text-slate-600 hover:border-purple-200'}`}>🏗️ Site Visit</button>
                <button type="button" onClick={() => setScheduleVisitForm({...scheduleVisitForm, meeting_type: 'OBM', obm_purpose: 'Project Discussion'})} className={`p-2.5 rounded-xl text-xs font-medium border transition-all flex items-center justify-center gap-2 ${scheduleVisitForm.meeting_type === 'OBM' ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-slate-200 bg-white text-slate-600 hover:border-amber-200'}`}>☕ OBM</button>
              </div>
            </div>
            
            {/* OBM Purpose */}
            {scheduleVisitForm.meeting_type === 'OBM' && (
              <div>
                <Label className="text-xs">Purpose *</Label>
                <Select value={scheduleVisitForm.obm_purpose} onValueChange={(val) => setScheduleVisitForm({...scheduleVisitForm, obm_purpose: val})}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Select purpose..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Project Discussion">Project Discussion</SelectItem>
                    <SelectItem value="Requirement Discussion">Requirement Discussion</SelectItem>
                    <SelectItem value="Negotiation">Negotiation</SelectItem>
                    <SelectItem value="Relationship Meeting">Relationship Meeting</SelectItem>
                    <SelectItem value="Document Collection">Document Collection</SelectItem>
                    <SelectItem value="Gift Delivery">🎁 Gift Delivery</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label className="text-xs">{scheduleVisitForm.meeting_type === 'Site Visit' ? 'Project *' : 'Related Project'}</Label>
              <ProjectSearchPicker
                value={scheduleVisitForm.project_id ? { project_id: scheduleVisitForm.project_id, project_name: scheduleVisitForm.project_name } : null}
                onChange={(project) => {
                  if (project) {
                    setScheduleVisitForm({...scheduleVisitForm, project_id: project.project_id, project_name: project.project_name});
                  } else {
                    setScheduleVisitForm({...scheduleVisitForm, project_id: '', project_name: ''});
                  }
                }}
                userCity={user?.city}
                className="mt-1"
                placeholder="Search project..."
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Date *</Label><Input type="date" value={scheduleVisitForm.visit_date} onChange={(e) => setScheduleVisitForm({...scheduleVisitForm, visit_date: e.target.value})} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs">Time *</Label><Input type="time" value={scheduleVisitForm.visit_time} onChange={(e) => setScheduleVisitForm({...scheduleVisitForm, visit_time: e.target.value})} className="mt-1 rounded-xl" /></div>
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea value={scheduleVisitForm.notes} onChange={(e) => setScheduleVisitForm({...scheduleVisitForm, notes: e.target.value})} rows={2} className="mt-1 rounded-xl" placeholder="Any notes for the meeting..." />
            </div>
            <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-xl">
              <input
                type="checkbox"
                id="auto_reminder"
                checked={scheduleVisitForm.auto_reminder}
                onChange={(e) => setScheduleVisitForm({...scheduleVisitForm, auto_reminder: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="auto_reminder" className="text-xs text-blue-700 cursor-pointer">Enable auto-reminder</Label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setShowScheduleVisitDialog(false)}>Cancel</Button>
              <Button className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-700" onClick={handleScheduleVisit}>
                <CalendarClock size={16} className="mr-2" />
                {scheduleVisitForm.meeting_type === 'Site Visit' ? 'Schedule Visit' : 'Schedule OBM'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark Complete Dialog */}
      <Dialog open={showCompleteVisitDialog} onOpenChange={(open) => { if (!open) { setShowCompleteVisitDialog(false); setSelectedVisit(null); setCompleteVisitForm({ project_id: '', project_name: '', completed_date: new Date().toISOString().split('T')[0], completed_time: new Date().toTimeString().slice(0, 5), remarks: '', next_followup_date: '', next_followup_time: '', lead_warmth: '' }); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mark Visit as Completed</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            {selectedVisit && (
              <div className="p-3 bg-emerald-50 rounded-xl text-sm border border-emerald-200">
                <p className="font-medium text-emerald-900">{selectedVisit.project_name}</p>
                <p className="text-xs text-emerald-600">Scheduled: {formatDateTime(selectedVisit.visit_datetime)}</p>
              </div>
            )}
            <div>
              <Label className="text-xs">Confirm Project</Label>
              <ProjectSearchPicker
                value={completeVisitForm.project_id ? { project_id: completeVisitForm.project_id, project_name: completeVisitForm.project_name } : (selectedVisit ? { project_id: selectedVisit.project_id, project_name: selectedVisit.project_name } : null)}
                onChange={(project) => {
                  if (project) {
                    setCompleteVisitForm({...completeVisitForm, project_id: project.project_id, project_name: project.project_name});
                  } else {
                    setCompleteVisitForm({...completeVisitForm, project_id: '', project_name: ''});
                  }
                }}
                userCity={user?.city}
                className="mt-1"
                placeholder="Search project..."
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs flex items-center gap-1">Completed Date <span className="text-slate-400">(editable)</span></Label><Input type="date" value={completeVisitForm.completed_date} onChange={(e) => setCompleteVisitForm({...completeVisitForm, completed_date: e.target.value})} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs flex items-center gap-1">Time <span className="text-slate-400">(editable)</span></Label><Input type="time" value={completeVisitForm.completed_time} onChange={(e) => setCompleteVisitForm({...completeVisitForm, completed_time: e.target.value})} className="mt-1 rounded-xl" /></div>
            </div>
            
            {/* Lead Warmth Selection */}
            <div>
              <Label className="text-xs">Lead Warmth</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                <button type="button" onClick={() => setCompleteVisitForm({...completeVisitForm, lead_warmth: ''})} className={`p-2 rounded-lg text-xs font-medium border transition-all ${!completeVisitForm.lead_warmth ? 'border-slate-400 bg-slate-100 text-slate-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}>No Change</button>
                <button type="button" onClick={() => setCompleteVisitForm({...completeVisitForm, lead_warmth: 'Hot'})} className={`p-2 rounded-lg text-xs font-medium border transition-all ${completeVisitForm.lead_warmth === 'Hot' ? 'border-red-400 bg-red-50 text-red-700' : 'border-slate-200 bg-white text-slate-500 hover:border-red-200 hover:bg-red-50'}`}>🔥 Hot</button>
                <button type="button" onClick={() => setCompleteVisitForm({...completeVisitForm, lead_warmth: 'Warm'})} className={`p-2 rounded-lg text-xs font-medium border transition-all ${completeVisitForm.lead_warmth === 'Warm' ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-slate-200 bg-white text-slate-500 hover:border-amber-200 hover:bg-amber-50'}`}>☀️ Warm</button>
                <button type="button" onClick={() => setCompleteVisitForm({...completeVisitForm, lead_warmth: 'Cold'})} className={`p-2 rounded-lg text-xs font-medium border transition-all ${completeVisitForm.lead_warmth === 'Cold' ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-500 hover:border-blue-200 hover:bg-blue-50'}`}>❄️ Cold</button>
              </div>
            </div>
            
            <div>
              <Label className="text-xs">Visit Notes</Label>
              <p className="text-xs text-slate-400 mb-1">These will be saved to customer notes</p>
              <Textarea value={completeVisitForm.remarks} onChange={(e) => setCompleteVisitForm({...completeVisitForm, remarks: e.target.value})} rows={3} className="mt-1 rounded-xl" placeholder="How did the visit go? Client feedback, interests, concerns..." />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Next Follow-up</Label><Input type="date" value={completeVisitForm.next_followup_date} onChange={(e) => setCompleteVisitForm({...completeVisitForm, next_followup_date: e.target.value})} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs">Time</Label><Input type="time" value={completeVisitForm.next_followup_time} onChange={(e) => setCompleteVisitForm({...completeVisitForm, next_followup_time: e.target.value})} className="mt-1 rounded-xl" /></div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => { setShowCompleteVisitDialog(false); setSelectedVisit(null); setCompleteVisitForm({ project_id: '', project_name: '', completed_date: new Date().toISOString().split('T')[0], completed_time: new Date().toTimeString().slice(0, 5), remarks: '', next_followup_date: '', next_followup_time: '', lead_warmth: '' }); }}>Cancel</Button>
              <Button className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700" onClick={handleMarkVisitCompleted}>
                <CheckCircle size={16} className="mr-2" />
                Mark Completed
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Visit Dialog */}
      <Dialog open={showCancelVisitDialog} onOpenChange={(open) => { if (!open) { setShowCancelVisitDialog(false); setSelectedVisit(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cancel Visit</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            {selectedVisit && (
              <div className="p-3 bg-red-50 rounded-xl text-sm border border-red-200">
                <p className="font-medium text-red-900">{selectedVisit.project_name}</p>
                <p className="text-xs text-red-600">Scheduled: {formatDateTime(selectedVisit.visit_datetime)}</p>
              </div>
            )}
            <div>
              <Label className="text-xs">Cancel Reason *</Label>
              <Select value={cancelVisitForm.cancel_reason} onValueChange={(v) => setCancelVisitForm({...cancelVisitForm, cancel_reason: v})}>
                <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  {cancelReasons.map((reason) => (<SelectItem key={reason} value={reason}>{reason}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea value={cancelVisitForm.notes} onChange={(e) => setCancelVisitForm({...cancelVisitForm, notes: e.target.value})} rows={2} className="mt-1 rounded-xl" placeholder="Additional details..." />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => { setShowCancelVisitDialog(false); setSelectedVisit(null); }}>Go Back</Button>
              <Button className="flex-1 rounded-xl bg-red-600 hover:bg-red-700" onClick={handleCancelVisit} disabled={!cancelVisitForm.cancel_reason}>
                <X size={16} className="mr-2" />
                Cancel Visit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule Visit Dialog */}
      <Dialog open={showRescheduleVisitDialog} onOpenChange={(open) => { if (!open) { setShowRescheduleVisitDialog(false); setSelectedVisit(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reschedule Visit</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            {selectedVisit && (
              <div className="p-3 bg-amber-50 rounded-xl text-sm border border-amber-200">
                <p className="font-medium text-amber-900">{selectedVisit.project_name}</p>
                <p className="text-xs text-amber-600">Originally: {formatDateTime(selectedVisit.visit_datetime)}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">New Date *</Label><Input type="date" value={rescheduleForm.new_date} onChange={(e) => setRescheduleForm({...rescheduleForm, new_date: e.target.value})} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs">New Time *</Label><Input type="time" value={rescheduleForm.new_time} onChange={(e) => setRescheduleForm({...rescheduleForm, new_time: e.target.value})} className="mt-1 rounded-xl" /></div>
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea value={rescheduleForm.notes} onChange={(e) => setRescheduleForm({...rescheduleForm, notes: e.target.value})} rows={2} className="mt-1 rounded-xl" placeholder="Reason for rescheduling..." />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => { setShowRescheduleVisitDialog(false); setSelectedVisit(null); }}>Cancel</Button>
              <Button className="flex-1 rounded-xl bg-amber-600 hover:bg-amber-700" onClick={handleRescheduleVisit} disabled={!rescheduleForm.new_date || !rescheduleForm.new_time}>
                <RefreshCw size={16} className="mr-2" />
                Reschedule Visit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showFollowupDialog} onOpenChange={setShowFollowupDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Schedule Follow-up</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            {/* Sync indicator */}
            <div className="p-2 bg-blue-50 rounded-lg border border-blue-200 flex items-center gap-2">
              <span className="text-blue-600">📌</span>
              <p className="text-xs text-blue-700">
                This follow-up will automatically appear in <span className="font-semibold">My Tasks</span>
              </p>
            </div>
            <div><Label className="text-xs">Type</Label>
              <Select value={followupForm.followup_type} onValueChange={(v) => setFollowupForm({...followupForm, followup_type: v})}>
                <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{followupTypes.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Date *</Label><Input type="date" value={followupForm.due_date} onChange={(e) => setFollowupForm({...followupForm, due_date: e.target.value})} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs">Time</Label><Input type="time" value={followupForm.due_time} onChange={(e) => setFollowupForm({...followupForm, due_time: e.target.value})} className="mt-1 rounded-xl" /></div>
            </div>
            <div><Label className="text-xs">Notes *</Label><Textarea value={followupForm.notes} onChange={(e) => setFollowupForm({...followupForm, notes: e.target.value})} rows={2} className="mt-1 rounded-xl" /></div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setShowFollowupDialog(false)}>Cancel</Button>
              <Button className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0] rounded-xl" onClick={handleCreateFollowup}>Schedule</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showLostDialog} onOpenChange={setShowLostDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Submit for Lost Review</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-700">
                <span className="font-medium">Review Process:</span> This lead will be reviewed by City Head who can confirm as Lost, reopen, or reassign.
              </p>
            </div>
            <div><Label className="text-xs">Reason *</Label>
              <Select value={lostForm.lost_reason} onValueChange={(v) => setLostForm({...lostForm, lost_reason: v})}>
                <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>{lostReasons.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Final Remark *</Label><Textarea value={lostForm.lost_remark} onChange={(e) => setLostForm({...lostForm, lost_remark: e.target.value})} rows={2} className="mt-1 rounded-xl" placeholder="Why is this lead being marked lost?" /></div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setShowLostDialog(false)}>Cancel</Button>
              <Button className="flex-1 rounded-xl bg-amber-600 hover:bg-amber-700" onClick={handleMarkLost} disabled={!lostForm.lost_reason || !lostForm.lost_remark?.trim()}>Submit for Review</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Booking</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div className="p-2 bg-slate-50 rounded-xl text-xs"><strong>{customer.customer_name}</strong><br/><span className="text-slate-500">{customer.customer_email}</span></div>
            {/* Only show projects where customer has completed a site visit */}
            {(() => {
              const completedVisitProjects = (customer?.project_wise_visits || []).filter(
                project => project.visits?.some(v => v.status === 'COMPLETED')
              );
              
              if (completedVisitProjects.length === 0) {
                return (
                  <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-center">
                    <AlertTriangle size={20} className="mx-auto text-amber-500 mb-1" />
                    <p className="text-xs font-medium text-amber-800">No Completed Site Visits</p>
                    <p className="text-[10px] text-amber-600 mt-0.5">
                      A booking can only be created for projects where the customer has completed a site visit.
                    </p>
                  </div>
                );
              }
              
              return (
                <div>
                  <Label className="text-xs">Project (Visited) *</Label>
                  <Select 
                    value={bookingForm.project_id} 
                    onValueChange={(projectId) => {
                      const project = completedVisitProjects.find(p => p.project_id === projectId);
                      setBookingForm({
                        ...bookingForm, 
                        project_id: projectId, 
                        project_name: project?.project_name || ''
                      });
                    }}
                  >
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue placeholder="Select visited project" />
                    </SelectTrigger>
                    <SelectContent>
                      {completedVisitProjects.map((project) => {
                        const visitCount = project.visits?.filter(v => v.status === 'COMPLETED').length || 0;
                        return (
                          <SelectItem key={project.project_id} value={project.project_id}>
                            {project.project_name} ({visitCount} visit{visitCount !== 1 ? 's' : ''})
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500 mt-0.5">Only projects with completed visits shown</p>
                </div>
              );
            })()}
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Date *</Label><Input type="date" value={bookingForm.booking_date} onChange={(e) => setBookingForm({...bookingForm, booking_date: e.target.value})} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs">Value (₹ Lacs) *</Label><Input type="number" value={bookingForm.booking_value} onChange={(e) => setBookingForm({...bookingForm, booking_value: e.target.value})} className="mt-1 rounded-xl" placeholder="e.g., 50" /></div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setShowBookingDialog(false)}>Cancel</Button>
              <Button 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-xl" 
                onClick={handleCreateBooking}
                disabled={!bookingForm.project_id || !(customer?.project_wise_visits || []).some(p => p.visits?.some(v => v.status === 'COMPLETED'))}
              >
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showStageDialog} onOpenChange={setShowStageDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change Stage</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div><Label className="text-xs">Stage</Label>
              <Select value={stageForm.stage} onValueChange={(v) => setStageForm({...stageForm, stage: v})}>
                <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Hot">🔥 Hot</SelectItem>
                  <SelectItem value="Warm">☀️ Warm</SelectItem>
                  <SelectItem value="Cold">❄️ Cold</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {stageForm.stage !== customer.stage && (
              <div><Label className="text-xs">Reason *</Label><Textarea value={stageForm.reason} onChange={(e) => setStageForm({...stageForm, reason: e.target.value})} rows={2} className="mt-1 rounded-xl" /></div>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setShowStageDialog(false)}>Cancel</Button>
              <Button className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0] rounded-xl" onClick={handleChangeStage}>{stageForm.stage === customer.stage ? 'Confirm' : 'Update'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Customer</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div><Label className="text-xs">Name</Label><Input value={editForm.customer_name || ''} onChange={(e) => setEditForm({...editForm, customer_name: e.target.value})} className="mt-1 rounded-xl" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Budget Min (₹L)</Label><Input type="number" value={editForm.budget_min_lacs || ''} onChange={(e) => setEditForm({...editForm, budget_min_lacs: e.target.value})} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs">Budget Max (₹L)</Label><Input type="number" value={editForm.budget_max_lacs || ''} onChange={(e) => setEditForm({...editForm, budget_max_lacs: e.target.value})} className="mt-1 rounded-xl" /></div>
            </div>
            <div><Label className="text-xs">Locations</Label><Input value={Array.isArray(editForm.preferred_locations) ? editForm.preferred_locations.join(', ') : ''} onChange={(e) => setEditForm({...editForm, preferred_locations: e.target.value.split(',').map(l => l.trim()).filter(l => l)})} className="mt-1 rounded-xl" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Config</Label>
                <Select value={editForm.configuration || ''} onValueChange={(v) => setEditForm({...editForm, configuration: v})}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="1BHK">1 BHK</SelectItem><SelectItem value="2BHK">2 BHK</SelectItem><SelectItem value="3BHK">3 BHK</SelectItem><SelectItem value="4BHK+">4 BHK+</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Purpose</Label>
                <Select value={editForm.purpose || 'End-use'} onValueChange={(v) => setEditForm({...editForm, purpose: v})}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="End-use">End-use</SelectItem><SelectItem value="Investment">Investment</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0] rounded-xl" onClick={handleUpdateCustomer}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Notes Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="max-w-md sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote size={18} className="text-amber-600" />
              Add Note
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Textarea
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                placeholder="Add a note..."
                className="min-h-[120px] resize-none rounded-xl"
                data-testid="note-content-input"
              />
              <p className="text-xs text-slate-400 mt-1">
                {noteContent.length} characters
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => { setShowNoteDialog(false); setNoteContent(''); }} 
                className="flex-1 rounded-xl"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddNote} 
                disabled={!noteContent.trim() || notesLoading}
                className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0] rounded-xl"
                data-testid="save-note-btn"
              >
                {notesLoading ? 'Saving...' : 'Save Note'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Referrer Dialog */}
      <Dialog open={showReferralDialog} onOpenChange={setShowReferralDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift size={18} className="text-purple-600" />
              Add/Change Referrer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Referrer Name *</Label>
              <Input 
                value={referralForm.name} 
                onChange={(e) => setReferralForm({...referralForm, name: e.target.value})} 
                className="mt-1 rounded-xl" 
                placeholder="Full name of referrer"
              />
            </div>
            <div>
              <Label className="text-xs">Phone *</Label>
              <Input 
                value={referralForm.phone} 
                onChange={(e) => setReferralForm({...referralForm, phone: e.target.value})} 
                className="mt-1 rounded-xl" 
                placeholder="10-digit mobile number"
              />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input 
                type="email"
                value={referralForm.email} 
                onChange={(e) => setReferralForm({...referralForm, email: e.target.value})} 
                className="mt-1 rounded-xl" 
                placeholder="Optional"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setShowReferralDialog(false)}>
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0] rounded-xl" 
                onClick={handleAddReferral}
                disabled={!referralForm.name || !referralForm.phone}
              >
                Add Referral
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View All Notes Dialog */}
      <Dialog open={showAllNotesDialog} onOpenChange={setShowAllNotesDialog}>
        <DialogContent className="max-w-md sm:max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote size={18} className="text-amber-600" />
              All Notes ({customerNotes.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4 max-h-[60vh] overflow-y-auto">
            {customerNotes.map((note, idx) => (
              <div key={note.note_id || idx} className="p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{note.content}</p>
                <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                  <span>{note.created_by_name}</span>
                  <span>•</span>
                  <span>{formatDateTime(note.created_at)}</span>
                </div>
              </div>
            ))}
            {customerNotes.length === 0 && (
              <div className="text-center py-8">
                <StickyNote size={24} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">No notes yet</p>
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              onClick={() => setShowAllNotesDialog(false)} 
              className="flex-1 rounded-xl"
            >
              Close
            </Button>
            <Button 
              onClick={() => { setShowAllNotesDialog(false); setShowNoteDialog(true); }}
              className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0] rounded-xl"
            >
              <Plus size={16} className="mr-2" />
              Add Note
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Smart Note Dialog */}
      <Dialog open={showWhatsAppDialog} onOpenChange={setShowWhatsAppDialog}>
        <DialogContent className="max-w-md sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle size={18} className="text-green-600" />
              Share Note via WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-slate-50 rounded-xl">
              <p className="text-xs text-slate-500 mb-1">Original Note:</p>
              <p className="text-sm text-slate-700">{lastSavedNote}</p>
            </div>
            
            {/* Tone Selector */}
            <div>
              <Label className="text-sm font-medium">Message Tone</Label>
              <div className="flex gap-2 mt-2">
                {['Formal', 'Concise', 'Premium Luxury'].map((tone) => (
                  <Button
                    key={tone}
                    variant={whatsAppTone === tone ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setWhatsAppTone(tone);
                      setWhatsAppMessage('');
                    }}
                    className={`flex-1 text-xs rounded-xl ${whatsAppTone === tone ? 'bg-green-600 hover:bg-green-700' : ''}`}
                  >
                    {tone}
                  </Button>
                ))}
              </div>
            </div>

            {/* Convert Button */}
            {!whatsAppMessage && (
              <Button
                onClick={handleConvertToWhatsApp}
                disabled={whatsAppConverting}
                className="w-full bg-green-600 hover:bg-green-700 rounded-xl"
              >
                {whatsAppConverting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Converting with AI...
                  </>
                ) : (
                  <>
                    <MessageCircle size={16} className="mr-2" />
                    Convert to WhatsApp Message
                  </>
                )}
              </Button>
            )}

            {/* Generated Message Preview */}
            {whatsAppMessage && (
              <div>
                <Label className="text-sm font-medium">WhatsApp Message Preview</Label>
                <Textarea
                  value={whatsAppMessage}
                  onChange={(e) => setWhatsAppMessage(e.target.value)}
                  className="mt-2 min-h-[200px] text-sm rounded-xl"
                  placeholder="Generated message will appear here..."
                />
                <p className="text-xs text-slate-400 mt-1">
                  You can edit the message before sharing
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowWhatsAppDialog(false);
                  setWhatsAppMessage('');
                  setLastSavedNote('');
                }} 
                className="flex-1 rounded-xl"
              >
                Close
              </Button>
              
              {whatsAppMessage && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleCopyToClipboard}
                    className="flex-1 rounded-xl"
                  >
                    <Copy size={16} className="mr-2" />
                    Copy
                  </Button>
                  <Button
                    onClick={handleShareViaWhatsApp}
                    className="flex-1 bg-green-600 hover:bg-green-700 rounded-xl"
                  >
                    <ExternalLink size={16} className="mr-2" />
                    Share
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDetail;
